import { Box3, Color, Euler, InstancedMesh, Matrix4, Mesh, Quaternion, Vector3, type BufferGeometry, type Material, type Object3D } from 'three';
import { toDegrees } from './scene-math';

export interface RenderedMaterial {
  color: number;
  flatShading: boolean;
  /** Self-lit colour, 0 for a material that does not glow (the neon of #102 does). */
  emissive: number;
}

/** What the test hooks read of one named object of a scene (issue #53, #66). */
export interface RenderedObject {
  name: string;
  position: { x: number; y: number; z: number };
  visible: boolean;
  /** Rotation in degrees around each axis. */
  rotation: { x: number; y: number; z: number };
  /** Rotation around z in degrees (the pitch of the plane). */
  rotationZ: number;
  box: { min: [number, number, number]; max: [number, number, number] };
  /** Triangles of the object and everything under it. */
  triangles: number;
  materials: RenderedMaterial[];
  /** Pose of an animated model (`userData.pose`), when it declares one (issue #96). */
  pose?: string;
  /** Lowest opacity among the materials under the object (1 when opaque). */
  opacity: number;
  type?: string;
  instanceCount?: number;
  instanceOf?: string;
}

const trianglesOf = (geometry: BufferGeometry): number =>
  (geometry.index ? geometry.index.count : (geometry.getAttribute('position')?.count ?? 0)) / 3;

/** Every named object under the root, with the triangles and materials of its subtree. */
export function collectRenderedObjects(root: Object3D): RenderedObject[] {
  const objects: RenderedObject[] = [];
  const box = new Box3();
  const matrix = new Matrix4();
  const position = new Vector3();
  const rotation = new Euler();
  const quaternion = new Quaternion();
  const scale = new Vector3();
  const vertexColor = new Color();
  const visit = (object: Object3D) => {
    if (object.name) {
      if (object instanceof InstancedMesh) object.computeBoundingBox();
      box.setFromObject(object);
      let triangles = 0;
      let opacity = 1;
      const materials = new Map<string, RenderedMaterial>();
      object.traverse((child) => {
        if (!(child instanceof Mesh)) return;
        triangles += trianglesOf(child.geometry) * (child instanceof InstancedMesh ? child.count : 1);
        for (const material of Array.isArray(child.material) ? child.material : [child.material]) {
          const flat = material as Material & { color?: { getHex(): number }; emissive?: { getHex(): number }; flatShading?: boolean; vertexColors?: boolean };
          opacity = Math.min(opacity, material.opacity);
          const colors = child.geometry.getAttribute('color');
          const emissiveColors = child.geometry.getAttribute('skinEmissive');
          if (child instanceof InstancedMesh && child.instanceColor) {
            for (let index = 0; index < child.count; index++) {
              child.getColorAt(index, vertexColor);
              const hex = vertexColor.getHex();
              materials.set(`${material.uuid}:${hex}`, { color: hex, flatShading: flat.flatShading === true, emissive: flat.emissive?.getHex() ?? 0 });
            }
          } else if (flat.vertexColors && colors) {
            for (let index = 0; index < colors.count; index++) {
              const hex = vertexColor.fromBufferAttribute(colors, index).getHex();
              const emissive = emissiveColors ? new Color().fromBufferAttribute(emissiveColors, index).getHex() : flat.emissive?.getHex() ?? 0;
              materials.set(`${material.uuid}:${hex}:${emissive}`, { color: hex, flatShading: flat.flatShading === true, emissive });
            }
          } else materials.set(material.uuid, { color: flat.color?.getHex() ?? 0, flatShading: flat.flatShading === true, emissive: flat.emissive?.getHex() ?? 0 });
        }
      });
      objects.push({
        name: object.name,
        position: { x: object.position.x, y: object.position.y, z: object.position.z },
        visible: object.visible,
        rotation: { x: toDegrees(object.rotation.x), y: toDegrees(object.rotation.y), z: toDegrees(object.rotation.z) },
        rotationZ: toDegrees(object.rotation.z),
        box: { min: box.min.toArray() as [number, number, number], max: box.max.toArray() as [number, number, number] },
        triangles,
        materials: [...materials.values()],
        opacity,
        type: object instanceof InstancedMesh ? 'InstancedMesh' : object.type,
        ...(object instanceof InstancedMesh && { instanceCount: object.count }),
        ...(typeof object.userData.pose === 'string' && { pose: object.userData.pose }),
      });
      // Report the actual transforms being drawn, retaining entity names without creating scene objects.
      if (object instanceof InstancedMesh && Array.isArray(object.userData.instanceIds)) {
        object.geometry.computeBoundingBox();
        for (let index = 0; index < object.count; index++) {
          object.getMatrixAt(index, matrix);
          matrix.premultiply(object.matrixWorld);
          box.copy(object.geometry.boundingBox!).applyMatrix4(matrix);
          matrix.decompose(position, quaternion, scale);
          rotation.setFromQuaternion(quaternion);
          const instanceMaterials = object.instanceColor
            ? [{ color: (object.getColorAt(index, vertexColor), vertexColor.getHex()), flatShading: [...materials.values()].every((material) => material.flatShading), emissive: 0 }]
            : [...materials.values()];
          objects.push({
            name: object.userData.instanceIds[index] as string,
            position: { x: position.x, y: position.y, z: position.z },
            visible: object.visible,
            rotation: { x: toDegrees(rotation.x), y: toDegrees(rotation.y), z: toDegrees(rotation.z) },
            rotationZ: toDegrees(rotation.z),
            box: { min: box.min.toArray() as [number, number, number], max: box.max.toArray() as [number, number, number] },
            triangles: trianglesOf(object.geometry),
            materials: instanceMaterials, opacity,
            type: 'Instance', instanceOf: object.name,
          });
        }
      }
    }
    object.children.forEach(visit);
  };
  root.children.forEach(visit);
  return objects;
}
